import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../toast/toastContext";
import { SignInLink } from "./SignInLink";
import { queuePendingToast, reloadPortal } from "../toast/pendingToast";
import { Link, useNavigate } from "react-router-dom";
import { impersonateUser, revertImpersonation, searchImpersonationTargets, type PersonSummary } from "../../api/session";
import { useSession } from "../../session/SessionContext";
import type { Theme } from "../../theme/useTheme";
import { Modal } from "../admin/Modal";
import { formatPersonName } from "../shared/personDisplay";
import { MonthPicker } from "../MonthPicker";
import { useSelectedPeriod } from "../../hooks/useSelectedPeriod";
import { BrandLogo } from "./BrandLogo";
import { PeopleSearch } from "./PeopleSearch";
import { PortalNav } from "./PortalNav";
import { promptInstall, useInstallOption } from "../../pwa/installPrompt";
import styles from "./Header.module.css";
import { Tooltip } from "../shared/Tooltip";

const OthersConsumptionPermission = "consumption.read.others";

/**
 * Derives a two-character avatar initial from a display name or an email address.
 *
 * For multi-word display names (e.g. "Erik De Vrieze") and dotted/underscored email local-parts
 * (e.g. "dan.theman@rws.com"), this takes the first letter of the first word and the first letter
 * of the last word ("EV", "DT"). For a single-word local-part with no separator (e.g.
 * "tpearce@rws.com", conventionally first-initial-plus-surname), there is no word boundary to
 * split on, so this instead takes the first two characters ("TP") — anything before the "@" is
 * ignored, since the domain never contributes to a person's initials.
 */
function getInitials(nameOrEmail: string): string {
  const atIndex = nameOrEmail.indexOf("@");
  const local = atIndex >= 0 ? nameOrEmail.slice(0, atIndex) : nameOrEmail;
  const parts = local
    .trim()
    .split(/[\s._-]+/)
    .filter((part) => part.length > 0);

  if (parts.length === 0) {
    return "";
  }

  if (parts.length === 1) {
    const [single] = parts;
    const firstInitial = single[0]?.toUpperCase() ?? "";
    const secondInitial = single[1]?.toUpperCase() ?? firstInitial;
    return `${firstInitial}${secondInitial}`;
  }

  const firstInitial = parts[0][0]?.toUpperCase() ?? "";
  const lastInitial = parts[parts.length - 1][0]?.toUpperCase() ?? "";
  return `${firstInitial}${lastInitial}`;
}

/**
 * The shell's header: brand identity, a light/dark theme toggle, and the signed-in indicator
 * with sign out (or a sign-in prompt when signed out) — the whole of the header, deliberately,
 * with no nested menu (`QR-01`).
 *
 * @param props.theme - The active theme, passed through to {@link BrandLogo} and the toggle's label.
 * @param props.onToggleTheme - Switches between the light and dark theme.
 */
/** The icon drawn to the left of a user menu entry's text; the path data is the icon itself. */
function UserMenuIcon({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <svg className={styles.userMenuIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

export function Header({
  theme,
  onToggleTheme,
  iconsInFooter = false,
  onIconsInFooterChange,
}: {
  theme: Theme;
  onToggleTheme: () => void;
  /** True when the theme, feedback and help icons are currently shown in the footer instead. */
  iconsInFooter?: boolean;
  /** Called when the header runs out of room (or regains it) and the icons should move. */
  onIconsInFooterChange?: (iconsInFooter: boolean) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { snapshot, signOut } = useSession();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [impersonationOpen, setImpersonationOpen] = useState(false);
  const [impersonationQuery, setImpersonationQuery] = useState("");
  const [impersonationResults, setImpersonationResults] = useState<PersonSummary[]>([]);
  const [showImpersonationSearchHint, setShowImpersonationSearchHint] = useState(false);
  const [hasImpersonationSearchRun, setHasImpersonationSearchRun] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [periodStart, setPeriodStart] = useSelectedPeriod();
  const [periodMenuOpen, setPeriodMenuOpen] = useState(false);
  const installOption = useInstallOption();
  const [installHelpOpen, setInstallHelpOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const periodMenuRef = useRef<HTMLDivElement>(null);
  const searchDebounceRef = useRef<number | null>(null);
  const quickActionsRef = useRef<HTMLDivElement>(null);
  const quickActionsWidthRef = useRef(0);
  const [navSlack, setNavSlack] = useState<number | null>(null);
  const [iconNavSlack, setIconNavSlack] = useState<number | null>(null);
  const [brandCompact, setBrandCompact] = useState(false);
  const brandRef = useRef<HTMLDivElement>(null);
  const fullBrandWidthRef = useRef(0);
  const [periodCompact, setPeriodCompact] = useState(false);
  const periodButtonRef = useRef<HTMLButtonElement>(null);
  const fullPeriodWidthRef = useRef(0);
  // The people search collapses to its icon when the month picker drops its label, so the two are one step in the same budget of space.
  const searchRef = useRef<HTMLDivElement>(null);
  const fullSearchWidthRef = useRef(0);

  useLayoutEffect(() => {
    if (!brandCompact && brandRef.current) fullBrandWidthRef.current = brandRef.current.offsetWidth;
    if (!periodCompact && periodButtonRef.current) fullPeriodWidthRef.current = periodButtonRef.current.offsetWidth;
    if (!periodCompact && searchRef.current) fullSearchWidthRef.current = searchRef.current.offsetWidth;
  });

  // Once the tabs are icons and still do not fit, the month label gives way first, and the people search with it, then the product name, before the icons
  // collapse into the menu, which is the last resort. The choice is made from the spare width the icons would have with both
  // in full, never from the previous frame's choice, so a window sitting on a boundary settles instead of flipping every frame.
  const nameDeltaRef = useRef(0);
  const periodLabelDeltaRef = useRef(0);
  useLayoutEffect(() => {
    if (iconNavSlack === null || !iconsInFooter) {
      if (brandCompact) setBrandCompact(false);
      if (periodCompact) setPeriodCompact(false);
      return;
    }
    if (brandCompact) nameDeltaRef.current = fullBrandWidthRef.current - (brandRef.current?.offsetWidth ?? 0);
    if (periodCompact) {
      const searchSaved = searchRef.current ? fullSearchWidthRef.current - searchRef.current.offsetWidth : 0;
      periodLabelDeltaRef.current = fullPeriodWidthRef.current - (periodButtonRef.current?.offsetWidth ?? 0) + searchSaved;
    }
    const slackWithBothFull = iconNavSlack - (brandCompact ? nameDeltaRef.current : 0) - (periodCompact ? periodLabelDeltaRef.current : 0);
    const wantPeriodCompact = slackWithBothFull < 0;
    const wantBrandCompact = slackWithBothFull + periodLabelDeltaRef.current < 0;
    if (wantPeriodCompact !== periodCompact) setPeriodCompact(wantPeriodCompact);
    if (wantBrandCompact !== brandCompact) setBrandCompact(wantBrandCompact);
    // Runs on a new measurement only: the spare width is read under the layout that was in place when it was taken, so acting on
    // it again after our own change (with the old figure) would undo that change and loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iconNavSlack, iconsInFooter]);

  useLayoutEffect(() => {
    const group = quickActionsRef.current;
    if (!group) return;
    const gap = group.parentElement ? parseFloat(getComputedStyle(group.parentElement).columnGap) || 0 : 0;
    quickActionsWidthRef.current = group.offsetWidth + gap;
  });

  // Overlap detection: free space by moving the icons to the footer first; the tabs only collapse
  // into the hamburger once the icons are already out of the way. The icons return only when the
  // tabs would still fit with them back in place.
  useLayoutEffect(() => {
    if (!onIconsInFooterChange) return;
    if (navSlack === null) {
      if (iconsInFooter) onIconsInFooterChange(false);
      return;
    }
    if (!iconsInFooter && navSlack < 0) onIconsInFooterChange(true);
    else if (iconsInFooter && navSlack >= quickActionsWidthRef.current) onIconsInFooterChange(false);
  }, [navSlack, iconsInFooter, onIconsInFooterChange]);

  useEffect(() => {
    function handlePointerDown(event: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
        setLanguageMenuOpen(false);
      }
      if (periodMenuRef.current && !periodMenuRef.current.contains(event.target as Node)) {
        setPeriodMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  const selectedPeriodLabel = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${periodStart}T00:00:00Z`));
  const isSignedIn = snapshot.status === "signed-in" || snapshot.status === "unclassified";
  const initials = isSignedIn ? getInitials(formatPersonName(snapshot.person)) : "";
  // An impersonated session is not the administrator's own, so it cannot start another impersonation: no nesting.
  const isImpersonated = (snapshot.status === "signed-in" || snapshot.status === "unclassified") && snapshot.impersonated;
  const canImpersonate = snapshot.status === "signed-in" && snapshot.permissions.includes("identity.impersonate") && !isImpersonated;
  // The people search (AIA-118) is for callers who may see other people's consumption.
  const canSearchPeople = snapshot.status === "signed-in" && snapshot.permissions.includes(OthersConsumptionPermission);
  const languages = [
    { code: "en", label: t("languagePicker.english") },
    { code: "ro", label: t("languagePicker.romanian") },
  ] as const;
  const activeLanguage = i18n.resolvedLanguage ?? i18n.language;

  const handleSignOut = async () => {
    // Leave the current page first. Once the session is gone, a deep link would send the browser straight back to sign-in.
    void navigate("/");
    await signOut();
  };

  const closeImpersonationDialog = () => {
    setImpersonationOpen(false);
    setImpersonationQuery("");
    setImpersonationResults([]);
    setShowImpersonationSearchHint(false);
    setHasImpersonationSearchRun(false);
  };

  const handleImpersonationSearch = useCallback(
    async (searchText: string): Promise<void> => {
      const search = searchText.trim();
      if (search.length === 0) {
        setImpersonationResults([]);
        setShowImpersonationSearchHint(false);
        setHasImpersonationSearchRun(false);
        return;
      }

      setHasImpersonationSearchRun(true);
      setIsSearching(true);

      const result = await searchImpersonationTargets(search);
      setIsSearching(false);

      if (result.kind === "ok") {
        setImpersonationResults(result.response.items);
        setShowImpersonationSearchHint(result.response.totalCount > 10);
        return;
      }

      if (result.kind === "denied") {
        toast.show(t("states.permissionDenied.defaultDescription"), "error");
        return;
      }

      toast.show(result.message, "error");
    },
    [t, toast],
  );

  useEffect(() => {
    if (!impersonationOpen) {
      if (searchDebounceRef.current !== null) {
        window.clearTimeout(searchDebounceRef.current);
      }

      return;
    }

    const search = impersonationQuery.trim();
    if (search.length === 0) {
      setImpersonationResults([]);
      setShowImpersonationSearchHint(false);
      setHasImpersonationSearchRun(false);
      return;
    }

    if (searchDebounceRef.current !== null) {
      window.clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = window.setTimeout(() => {
      void handleImpersonationSearch(search);
    }, 300);

    return () => {
      if (searchDebounceRef.current !== null) {
        window.clearTimeout(searchDebounceRef.current);
      }
    };
  }, [impersonationOpen, impersonationQuery, t, handleImpersonationSearch]);

  async function handleRevertIdentity(): Promise<void> {
    setMenuOpen(false);
    const result = await revertImpersonation();
    if (result.kind === "ok") {
      // A full reload, not a client-side navigation: every page's data belongs to the identity being left.
      queuePendingToast(t("session.revertIdentityDone"), "success");
      reloadPortal();
      return;
    }

    toast.show(result.message, "error");
  }

  async function handleImpersonate(personId: string): Promise<void> {
    setIsImpersonating(true);

    const result = await impersonateUser(personId);
    setIsImpersonating(false);

    if (result.kind === "ok") {
      closeImpersonationDialog();
      reloadPortal();
      return;
    }

    if (result.kind === "denied") {
      toast.show(t("states.permissionDenied.defaultDescription"), "error");
      return;
    }

    toast.show(result.message, "error");
  }

  return (
    <header className={styles.header} data-toast-anchor>
      <div className={styles.brand} ref={brandRef}>
        <BrandLogo theme={theme} compact={brandCompact} />
      </div>
      {isSignedIn && (
        <div className={styles.navArea}>
          <PortalNav allowCollapse={iconsInFooter} allowMenu={brandCompact} onSlackChange={setNavSlack} onIconSlackChange={setIconNavSlack} />
        </div>
      )}
      <div className={styles.actions}>
        {canSearchPeople && <PeopleSearch compact={periodCompact} rootRef={searchRef} />}
        {isSignedIn && (
          <div className={styles.periodMenu} ref={periodMenuRef}>
            <button
              ref={periodButtonRef}
              type="button"
              className={`${styles.feedbackLink} ${styles.periodButton} ${periodCompact ? styles.periodButtonCompact : ""}`}
              aria-label={`${t("monthPicker.label")}: ${selectedPeriodLabel}`}
              aria-haspopup="menu"
              aria-expanded={periodMenuOpen}
              onClick={() => setPeriodMenuOpen((open) => !open)}
            >
              {!periodCompact && <span className={styles.periodLabel}>{selectedPeriodLabel}</span>}
              <svg className={styles.feedbackIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="3" y="4" width="18" height="17" rx="2" />
                <path d="M16 2v4M8 2v4M3 10h18" />
              </svg>
            </button>
            {periodMenuOpen && (
              <div className={styles.periodMenuPanel} role="menu">
                <MonthPicker
                  periodStart={periodStart}
                  onChange={setPeriodStart}
                  label={t("monthPicker.label")}
                  focusOnMount
                  onClose={() => {
                    setPeriodMenuOpen(false);
                    periodButtonRef.current?.focus();
                  }}
                />
              </div>
            )}
          </div>
        )}
        {!iconsInFooter && (
        <div className={styles.quickActions} ref={quickActionsRef}>
        <button
          type="button"
          className={styles.feedbackLink}
          aria-label={theme === "dark" ? t("theme.switchToLight") : t("theme.switchToDark")}
          onClick={onToggleTheme}
        >
          {theme === "dark" ? (
            <svg className={styles.feedbackIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2m0 16v2M4.93 4.93l1.42 1.42m11.3 11.3 1.42 1.42M2 12h2m16 0h2M4.93 19.07l1.42-1.42m11.3-11.3 1.42-1.42" />
            </svg>
          ) : (
            <svg className={styles.feedbackIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20.9 13A9 9 0 0 1 11 3.1 9 9 0 1 0 20.9 13Z" />
            </svg>
          )}
        </button>
        {isSignedIn && (
          <>
            <Tooltip label={t("feedback.giveFeedback")} placement="below" align="end">
              <a
                className={styles.feedbackLink}
                href="https://github.com/rws-product-technology/aia-portal/issues"
                target="_blank"
                rel="noreferrer"
                aria-label={t("feedback.giveFeedback")}
              >
                <svg
                  className={styles.feedbackIcon}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                </svg>
              </a>
            </Tooltip>
            <Tooltip label={t("help.openHelp")} placement="below" align="end">
              <a
                className={styles.feedbackLink}
                href="/help"
                target="_blank"
                rel="noreferrer"
                aria-label={t("help.openHelp")}
              >
                <svg
                  className={styles.feedbackIcon}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 2-3 4" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </a>
            </Tooltip>
          </>
        )}
        </div>
        )}
        {isSignedIn ? (
          <div className={styles.userMenu} ref={menuRef}>
            <button
              type="button"
              className={`${styles.userMenuButton} ${isImpersonated ? styles.userMenuButtonImpersonating : ""}`}
              aria-label={t("session.signedInAs", {
                name: formatPersonName(snapshot.person),
              })}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => {
                setMenuOpen((open) => !open);
                setLanguageMenuOpen(false);
              }}
            >
              <span className={styles.userAvatar}>{initials}</span>
            </button>
            {menuOpen && (
              <div className={styles.userMenuPanel} role="menu" aria-label={t("session.signedInAs", { name: formatPersonName(snapshot.person) })}>
                <div className={styles.userMenuHeader}>
                  <span className={styles.userAvatarLarge}>{initials}</span>
                  <div>
                    <div className={styles.userMenuName}>{formatPersonName(snapshot.person)}</div>
                    <div className={styles.userMenuEmail}>{snapshot.person.email}</div>
                  </div>
                </div>
                <div className={styles.languageMenu}>
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.userMenuItem}
                    aria-haspopup="menu"
                    aria-expanded={languageMenuOpen}
                    onClick={() => setLanguageMenuOpen((open) => !open)}
                  >
                    <UserMenuIcon>
                      <circle cx="12" cy="12" r="10" />
                      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </UserMenuIcon>
                    <span>{t("languagePicker.label")}</span>
                    <span className={`${styles.submenuIndicator} ${languageMenuOpen ? styles.submenuIndicatorOpen : ""}`} aria-hidden="true">›</span>
                  </button>
                  {languageMenuOpen && (
                    <div className={styles.languageMenuPanel} role="menu" aria-label={t("languagePicker.label")}>
                      {languages.map(({ code, label }) => (
                        <button
                          key={code}
                          type="button"
                          role="menuitemradio"
                          aria-checked={activeLanguage === code}
                          className={styles.userMenuItem}
                          onClick={() => {
                            void i18n.changeLanguage(code);
                            setLanguageMenuOpen(false);
                            setMenuOpen(false);
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Link
                  to="/connected-applications"
                  role="menuitem"
                  className={styles.userMenuItem}
                  onClick={() => setMenuOpen(false)}
                >
                  <UserMenuIcon>
                    <path d="M9 2v6M15 2v6M6 8h12v4a6 6 0 0 1-12 0zM12 18v4" />
                  </UserMenuIcon>
                  {t("connectedApps.title")}
                </Link>
                <Link
                  to="/preferences"
                  role="menuitem"
                  className={styles.userMenuItem}
                  onClick={() => setMenuOpen(false)}
                >
                  <UserMenuIcon>
                    <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
                  </UserMenuIcon>
                  {t("preferences.title")}
                </Link>
                {/* Where the browser offers installation it shows its own dialog; on an iPhone or iPad the portal can only explain how. */}
                {installOption !== "none" && (
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.userMenuItem}
                    onClick={() => {
                      setMenuOpen(false);
                      if (installOption === "prompt") {
                        void promptInstall();
                      } else {
                        setInstallHelpOpen(true);
                      }
                    }}
                  >
                    <UserMenuIcon>
                      <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
                    </UserMenuIcon>
                    {t("install.menuItem")}
                  </button>
                )}
                {isImpersonated && (
                  <button type="button" className={styles.userMenuItem} onClick={() => void handleRevertIdentity()}>
                    <UserMenuIcon>
                      <path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5" />
                    </UserMenuIcon>
                    {t("session.revertIdentity")}
                  </button>
                )}
                {canImpersonate && (
                  <button
                    type="button"
                    className={styles.userMenuItem}
                    onClick={() => {
                      setMenuOpen(false);
                      setImpersonationOpen(true);
                    }}
                  >
                    <UserMenuIcon>
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                    </UserMenuIcon>
                    {t("session.impersonate")}
                  </button>
                )}
                <hr className={styles.userMenuDivider} />
                <button type="button" className={styles.userMenuItem} onClick={() => void handleSignOut()}>
                  <UserMenuIcon>
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                  </UserMenuIcon>
                  {t("session.signOut")}
                </button>
              </div>
            )}
          </div>
        ) : (
          <SignInLink />
        )}
      </div>
      {installHelpOpen && (
        <Modal title={t("install.iosTitle")} onClose={() => setInstallHelpOpen(false)}>
          <div className={styles.installHelp}>
            <p>{t("install.iosIntro")}</p>
            <ol>
              <li>{t("install.iosStepShare")}</li>
              <li>{t("install.iosStepAdd")}</li>
              <li>{t("install.iosStepConfirm")}</li>
            </ol>
          </div>
        </Modal>
      )}
      {impersonationOpen && (
        <Modal title={t("session.impersonateTitle")} onClose={closeImpersonationDialog}>
          <div className={styles.impersonationDialog}>
            <p>{t("session.impersonateDescription")}</p>
            <div className={styles.impersonationSearchForm}>
              <label className={styles.impersonationField}>
                <span>{t("session.impersonateSearchLabel")}</span>
                <input
                  type="search"
                  data-autofocus
                  value={impersonationQuery}
                  onChange={(event) => setImpersonationQuery(event.target.value)}
                  placeholder={t("session.impersonateSearchPlaceholder")}
                  aria-busy={isSearching}
                />
              </label>
            </div>
            {impersonationResults.length > 0 ? (
              <ul className={styles.impersonationResults}>
                {impersonationResults.map((person) => (
                  <li key={person.id}>
                    <button type="button" className={styles.impersonationResultButton} onClick={() => void handleImpersonate(person.id)} disabled={isImpersonating}>
                      <span className={styles.impersonationName}>{person.displayName || person.email}</span>
                      <span className={styles.impersonationEmail}>{person.email}</span>
                      <span className={styles.impersonationAction}>{t("session.impersonateUse")}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              hasImpersonationSearchRun && !isSearching && impersonationResults.length === 0 && <p className={styles.impersonationNotice}>{t("session.impersonateNoResults")}</p>
            )}
            {showImpersonationSearchHint && <p className={styles.impersonationNotice}>{t("session.impersonateRefineSearch")}</p>}
            <p className={styles.impersonationNotice}>{t("session.impersonateRevert")}</p>
          </div>
        </Modal>
      )}
    </header>
  );
}
